import { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useDarkMode } from '../App'

interface AdminRouteProps {
  children: ReactNode
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isAdmin, isLoading, user } = useAuth()
  const { isDark } = useDarkMode()

  // Show loading state
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4 ${
            isDark ? 'border-white' : 'border-gray-900'
          }`}></div>
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
            Checking authentication...
          </p>
        </div>
      </div>
    )
  }

  // Show not authenticated message
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className={`max-w-md mx-auto text-center p-8 rounded-lg ${
          isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isDark ? 'bg-red-500/20' : 'bg-red-100'
          }`}>
            <svg className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Authentication Required
          </h2>
          
          <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            You need to sign in to access the admin area.
          </p>
          
          <div className="space-y-3">
            <a
              href="/.auth/login/aad"
              className={`block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors ${
                isDark 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              Sign in with Azure AD
            </a>
            
            <a
              href="/.auth/login/github"
              className={`block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors border ${
                isDark 
                  ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
            >
              Sign in with GitHub
            </a>
          </div>
        </div>
      </div>
    )
  }

  // Show not authorized message
  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className={`max-w-md mx-auto text-center p-8 rounded-lg ${
          isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isDark ? 'bg-orange-500/20' : 'bg-orange-100'
          }`}>
            <svg className={`w-8 h-8 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Access Denied
          </h2>
          
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            You don't have admin privileges to access this area.
          </p>
          
          <div className={`mb-6 p-4 rounded-lg ${
            isDark ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Signed in as: <strong>{user?.userDetails}</strong>
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Roles: {user?.userRoles?.join(', ') || 'No roles assigned'}
            </p>
          </div>
          
          <div className="space-y-3">
            <a
              href="/"
              className={`block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors ${
                isDark 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              Go to Home
            </a>
            
            <a
              href="/.auth/logout"
              className={`block w-full py-3 px-4 rounded-lg font-medium text-center transition-colors border ${
                isDark 
                  ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
            >
              Sign Out
            </a>
          </div>
        </div>
      </div>
    )
  }

  // User is authenticated and has admin role
  return <>{children}</>
}